import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
} from 'react-native';
import {Image} from 'expo-image';
import {useTranslation} from 'react-i18next';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {ChevronRight} from 'lucide-react-native';
import {router} from 'expo-router';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";
import {AppDataOrganization} from "@/data/app-data-interfaces";
import {useSetLastSelectedOrg} from "@/data/user-data-hooks";
import {useLogout} from "@/auth/useLogout";
import Alert from "@/lib/Alert";

interface OrganizationSelectionScreenProps {
    organizations: { id: string; organization: AppDataOrganization }[];
}

export const OrganizationSelectionScreen = ({organizations}: OrganizationSelectionScreenProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme, insets);
    const setLastSelectedOrg = useSetLastSelectedOrg();
    const {logout} = useLogout();

    const handleSelectOrg = (orgId: string) => {
        setLastSelectedOrg.mutate(orgId);
        router.replace(`/organization/${orgId}`);
    };

    const handleLogout = () => {
        Alert.alert(
            t('auth.logout.confirmTitle'),
            t('auth.logout.confirmMessage'),
            [
                {text: t('common.cancel'), style: 'cancel'},
                {
                    text: t('auth.logout.button'),
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            await logout();
                        } catch (error) {
                            console.error('Logout failed:', error);
                            Alert.alert(t('common.error'), t('auth.logout.error'));
                        }
                    }
                }
            ]
        );
    };

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    return (
        <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
            <View style={styles.header}>
                <Text style={styles.title}>{t('orgSelection.title')}</Text>
                <Text style={styles.subtitle}>{t('orgSelection.subtitle')}</Text>
            </View>

            <View style={styles.orgList}>
                {organizations.map(({id, organization}) => (
                    <TouchableOpacity
                        key={id}
                        style={styles.orgCard}
                        onPress={() => handleSelectOrg(id)}
                        activeOpacity={0.7}
                    >
                        <View style={styles.orgCardContent}>
                            <View style={styles.orgIcon}>
                                {organization.icon ? (
                                    <Image
                                        source={{uri: organization.icon}}
                                        placeholder={{blurhash: organization.iconBlurhash}}
                                        style={styles.orgIconImage}
                                        contentFit="contain"
                                        cachePolicy="memory-disk"
                                    />
                                ) : (
                                    <Text style={styles.orgInitials}>
                                        {getInitials(organization.name)}
                                    </Text>
                                )}
                            </View>
                            <View style={styles.orgInfo}>
                                <Text style={styles.orgName} numberOfLines={1}>
                                    {organization.name}
                                </Text>
                                {organization.subtitle && (
                                    <Text style={styles.orgSubtitle} numberOfLines={1}>
                                        {organization.subtitle}
                                    </Text>
                                )}
                            </View>
                            <ChevronRight size={20} color={theme.colors.textSecondary}/>
                        </View>
                    </TouchableOpacity>
                ))}
            </View>

            <View style={styles.footer}>
                <TouchableOpacity
                    style={styles.logoutButton}
                    onPress={handleLogout}
                >
                    <Text style={styles.logoutButtonText}>{t('auth.logout.button')}</Text>
                </TouchableOpacity>
            </View>
        </ScrollView>
    );
};

const createStyles = (theme: ThemeType, insets: { top: number; bottom: number }) =>
    StyleSheet.create({
        container: {
            flex: 1,
            backgroundColor: theme.colors.background,
        },
        contentContainer: {
            flexGrow: 1,
            paddingTop: insets.top + theme.spacing.xl,
            paddingBottom: insets.bottom + theme.spacing.lg,
            paddingHorizontal: theme.spacing.lg,
        },
        header: {
            marginBottom: theme.spacing.xl,
            alignItems: 'center',
        },
        title: {
            fontSize: theme.fontSize.xxl,
            fontWeight: 'bold',
            color: theme.colors.text,
            textAlign: 'center',
            marginBottom: theme.spacing.sm,
        },
        subtitle: {
            fontSize: theme.fontSize.md,
            color: theme.colors.textSecondary,
            textAlign: 'center',
        },
        orgList: {
            gap: theme.spacing.sm,
        },
        orgCard: {
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            borderWidth: 1,
            borderColor: theme.colors.border,
            overflow: 'hidden',
        },
        orgCardContent: {
            flexDirection: 'row',
            alignItems: 'center',
            padding: theme.spacing.md,
        },
        orgIcon: {
            width: 48,
            height: 48,
            borderRadius: theme.borderRadius.md,
            backgroundColor: `${theme.colors.border}80`,
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: theme.spacing.md,
            overflow: 'hidden',
        },
        orgIconImage: {
            width: '100%',
            height: '100%',
        },
        orgInitials: {
            fontSize: theme.fontSize.lg,
            fontWeight: 'bold',
            color: theme.colors.text,
        },
        orgInfo: {
            flex: 1,
            marginRight: theme.spacing.sm,
        },
        orgName: {
            fontSize: theme.fontSize.lg,
            fontWeight: '600',
            color: theme.colors.text,
            marginBottom: 2,
        },
        orgSubtitle: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
        },
        footer: {
            marginTop: 'auto',
            paddingTop: theme.spacing.xl,
            alignItems: 'center',
        },
        logoutButton: {
            backgroundColor: theme.colors.error,
            paddingVertical: theme.spacing.md,
            paddingHorizontal: theme.spacing.xl,
            borderRadius: theme.borderRadius.md,
            minWidth: 120,
            alignItems: 'center',
        },
        logoutButtonText: {
            color: '#ffffff',
            fontSize: theme.fontSize.md,
            fontWeight: '600',
        },
    });
